import { API_CONFIG, getApiUrl, getAuthHeaders, getDefaultHeaders } from '../../config/api';
import { sessionStorageUtils } from './sessionStorage';
import { toast } from './toast';

// API Response interface
export interface ApiResponse<T> {
  data?: T;
  message?: string;
  success?: boolean;
  error?: string;
}

// API Error class
export class ApiError extends Error {
  status: number;
  response?: any;

  constructor(message: string, status: number, response?: any) {
    super(message);
    this.status = status;
    this.response = response;
  }
}

// API Client class
class ApiClient {
  private timeout: number;
  private onTokenExpired?: () => void;

  constructor() {
    this.timeout = API_CONFIG.TIMEOUT;
  }

  // Set callback for token expiration
  setTokenExpiredCallback(callback: () => void) {
    this.onTokenExpired = callback;
  }

  // Generic request method
  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    token?: string
  ): Promise<T> {
    const url = getApiUrl(endpoint);

    const headers = token 
      ? getAuthHeaders(token)
      : getDefaultHeaders();

    const config: RequestInit = {
      ...options,
      headers: {
        ...headers,
        ...options.headers,
      },
    };

    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), this.timeout);

      const response = await fetch(url, {
        ...config,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        
        // Handle token expiration (401 Unauthorized or 403 Forbidden)
        if (response.status === 401 || response.status === 403) {
          // Clear token and trigger logout
          sessionStorageUtils.removeToken();
          if (this.onTokenExpired) {
            this.onTokenExpired();
          }
          throw new ApiError(
            'Session expired. Please login again.',
            response.status,
            errorData
          );
        }
        
        const message = errorData.message || `HTTP ${response.status}: ${response.statusText}`;
        toast.showError(message);
        throw new ApiError(message, response.status, errorData);
      }

      const data = await response.json();
      return data;
    } catch (error) {
      if (error instanceof ApiError) {
        // Already toasted above
        throw error;
      }
      
      if (error instanceof Error && error.name === 'AbortError') {
        throw new ApiError('Request timeout', 408);
      }
      
      const msg = error instanceof Error ? error.message : 'Network error';
      toast.showError(msg);
      throw new ApiError(msg, 0);
    }
  }

  // GET request
  async get<T>(endpoint: string, token?: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' }, token);
  }

  // POST request
  async post<T>(endpoint: string, data?: any, token?: string): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'POST',
        body: data ? JSON.stringify(data) : undefined,
      },
      token
    );
  }

  // PUT request
  async put<T>(endpoint: string, data?: any, token?: string): Promise<T> {
    return this.request<T>(
      endpoint,
      {
        method: 'PUT',
        body: data ? JSON.stringify(data) : undefined,
      },
      token
    );
  }

  // DELETE request
  async delete<T>(endpoint: string, token?: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' }, token);
  }

  // Health check
  async healthCheck(): Promise<{ status: string }> {
    return this.get<{ status: string }>(API_CONFIG.ENDPOINTS.HEALTH);
  }

  // Database connectivity test
  async databaseCheck(): Promise<{ status: string; timestamp: string }> {
    return this.get<{ status: string; timestamp: string }>(API_CONFIG.ENDPOINTS.DATABASE);
  }
}

// Export singleton instance
export const apiClient = new ApiClient();
